import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import DraggablePanel from './DraggablePanel';
import AutocompleteDropdown from './AutocompleteDropdown';
import DomainSelect from './DomainSelect';
import TagAutocompleteInput from './TagAutocompleteInput';
import RedundancySetSection from './RedundancySetSection';
import type {
  BoundingBox,
  InstrumentCoreFieldValue,
  InstrumentCoreFields,
  InstrumentTagStatus,
  LinkedInstrumentConnectionType,
  PidDocument,
  PnidEquipment,
  PnidHyperlinkRef,
  PnidInstrument,
  PnidLinkedInstrumentLink,
  PnidLinkedPfdStreamRef,
  PnidInstrumentUpdate,
  PnidNode,
  PnidWizardState,
} from '../types';
import { BranchDirection, LINKED_INSTRUMENT_CONNECTION_TYPES, instrumentRegions } from '../types';
import { assetApi, pnidApi } from '../api';
import {
  CORE_FORM_FIELDS,
  type CoreFieldDefinition,
  buildCoreFieldOptions,
  buildCoreInstrumentUpdates,
  fillEmptyCoreFields,
  getCoreFormState,
  haveCoreFormValuesChanged,
  isCoreFieldVisible,
  applyIdentifiedCoreFields,
  sanitizeCoreFormState,
  type TouchedCoreFields,
  type SharedInstrumentEditorState,
} from '../utils/instrumentFormSchema';
import { getInstrumentDisplayName, isGeneratedIdPrimaryInstrumentType } from '../utils/instrumentTypes';
import {
  formatInstrumentIdentity,
  getInstrumentPrimaryLabel,
  usesGeneratedIdAsPrimaryLabel,
} from '../utils/instrumentIdentity';
import { getInstrumentEffectiveContext } from '../utils/unifiedInstruments';
import SchemaFieldRenderer, { CustomRendererMap } from './SchemaFieldRenderer';
import { useFormSchema } from '../hooks/useFormSchemas';
import type { FormSchemaDefinition } from '../types/formSchema';

/** Ties the Domain row's label to its select, which is not nested inside it. */
const OWNER_DOMAIN_SELECT_ID = 'instrument-editor-owner-domain';
const DESCRIPTION_INPUT_ID = 'instrument-editor-description';

type LinkedInstrumentDraftRow = {
  row_id: string;
  instrument_id: string;
  instrument_ref: string;
  connection_type: LinkedInstrumentConnectionType | '';
};

type InstrumentReviewTarget = { kind: 'equipmentInstrument' | 'nodeInstrument'; id: string };

interface InstrumentEditorProps {
  instrument: PnidInstrument;
  position: { x: number; y: number };
  onSave: (id: string, updates: PnidInstrumentUpdate) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
  allNodes: PnidNode[];
  allEquipments: PnidEquipment[];
  allInstruments: Array<{ id: string; instrument_text: string | null; generated_id: string | null; instrument_type?: string | null }>;
  onHighlightEntity?: (id: string | null) => void;
  onOpenPnidHyperlink?: (link: PnidHyperlinkRef, options?: { newTab?: boolean }) => void;
  allDocuments?: PidDocument[];
  currentDocId?: string | null;
  /** The drawing's domain, which owns an instrument that names no owner. Without
   *  one the drawing belongs to no plant and shows no redundancy set. */
  currentDomainId?: string | null;
  currentPlant?: string | null;
  diagramMode?: 'pid' | 'pfd';
  linkedDocumentStates?: Record<string, PnidWizardState>;
  onLoadLinkedDocumentState?: (docId: string) => Promise<void>;
  onOpenLinkedDocument?: (target: {
    docId: string;
    targetEquipmentId?: string | null;
    targetNodeId?: string | null;
    newTab?: boolean;
  }) => void;
  /** Same-doc instruments list (for resolving chained context_type instrument → equipment/node) */
  docInstruments?: PnidInstrument[];
  /** Explicit review (green on canvas); same as Mark dialog / review tables */
  onMarkReviewed?: (target: InstrumentReviewTarget) => Promise<void> | void;
  /** When true, all editing controls are disabled (reviewers can still click Mark as Reviewed) */
  readOnly?: boolean;
  /** Temporary lock while a local canvas snapshot is being replaced by the backend copy. */
  latestInstrumentLoading?: boolean;
  latestInstrumentError?: string | null;
  /**
   * The identify answer for the box the SME has just drawn, keyed by storage key. It
   * pre-fills the form, and its presence — empty object included, for an answer that
   * failed — is what lets editing the tag ask again. Absent for an instrument opened
   * any other way, which the SME did not draw and has no answer to repeat.
   */
  identifyPrefill?: Record<string, unknown> | null;
  /**
   * Hands the marking's regions back to the canvas to be redrawn. The dialog hides
   * while that runs — it otherwise covers the part of the sheet being annotated —
   * and the caller reopens it when the SME finishes. Absent where the editor is
   * opened somewhere the canvas cannot be drawn on, which hides the button.
   */
  onEditRegions?: (instrumentId: string) => void;
}

/** How long the tag field must sit still before a re-identify is worth sending. */
const IDENTIFY_DEBOUNCE_MS = 400;

/** The panel's width. Callers anchoring the editor need it to place the panel. */
export const INSTRUMENT_EDITOR_WIDTH = 380;

/**
 * The core form's values together with the keys the SME has edited by hand.
 *
 * They are one state because they have to move together. Whether a value may be
 * replaced by an identify answer depends on who wrote it, so a writer that changes a
 * value without saying who it came from leaves the pair inconsistent — which is how a
 * mode picker's choice, written straight to the values, became something the next
 * answer overwrote. Every write goes through `coreFormReducer`, and every action it
 * accepts declares what it does to both halves.
 */
interface CoreFormState {
  values: InstrumentCoreFields;
  /** Never holds a key whose value came from the backend. */
  touched: TouchedCoreFields;
}

type CoreFormAction =
  /** The SME changed these fields by hand. */
  | { type: 'sme_edit'; patch: InstrumentCoreFields }
  /** An identify answer arrived. It owns every field the SME has not edited. */
  | { type: 'identified'; fields: Record<string, unknown> }
  /** The editor opened on another instrument. */
  | { type: 'reset'; instrument: PnidInstrument; identifiedFields: Record<string, unknown> }
  /** Visibility and option rules cleared what the form can no longer show. */
  | { type: 'sanitized'; values: InstrumentCoreFields };

function initialCoreFormState(
  instrument: PnidInstrument,
  identifiedFields: Record<string, unknown>,
): CoreFormState {
  return {
    values: fillEmptyCoreFields(getCoreFormState(instrument), identifiedFields),
    touched: new Set<string>(),
  };
}

function coreFormReducer(state: CoreFormState, action: CoreFormAction): CoreFormState {
  switch (action.type) {
    case 'sme_edit': {
      const touched = new Set(state.touched);
      for (const key of Object.keys(action.patch)) {
        touched.add(key);
      }
      return { values: { ...state.values, ...action.patch }, touched };
    }
    case 'identified':
      return {
        ...state,
        values: applyIdentifiedCoreFields(state.values, action.fields),
      };
    case 'reset':
      return initialCoreFormState(action.instrument, action.identifiedFields);
    case 'sanitized':
      return { ...state, values: action.values };
  }
}

function getInstrumentReviewTarget(
  inst: PnidInstrument,
  docInstruments: PnidInstrument[] | undefined
): InstrumentReviewTarget | null {
  if (!inst.id) return null;
  if (inst.context_type === 'equipment') return { kind: 'equipmentInstrument', id: inst.id };
  if (inst.context_type === 'node') return { kind: 'nodeInstrument', id: inst.id };

  const eff = getInstrumentEffectiveContext(inst, { instruments: docInstruments ?? [] });
  if (eff.context_type === 'equipment') return { kind: 'equipmentInstrument', id: inst.id };
  if (eff.context_type === 'node') return { kind: 'nodeInstrument', id: inst.id };

  // Fallback: PATCH uses instrument id regardless of parent context
  return { kind: 'nodeInstrument', id: inst.id };
}

// PFD subtype options, keyed by instrument_type. The PFD form uses a single
// `subtype` field whose option list depends on the selected instrument type.
const PFD_VALVE_SUBTYPE_OPTIONS = ['Gate Valve', 'Globe Valve', 'Ball Valve', 'Butterfly Valve', 'Check Valve'];
const PFD_OPC_SUBTYPE_OPTIONS = ['OPC In', 'OPC Out'];
// The PFD types that carry a subtype. A type outside this set has its stored subtype
// cleared on save, so a type the PFD offers must be listed here or it loses the value.
const PFD_SUBTYPE_TYPES = new Set(['Valve', 'Control Valve', 'OPC']);
function pfdSubtypeOptions(instrumentType: string): string[] {
  if (instrumentType === 'OPC') return PFD_OPC_SUBTYPE_OPTIONS;
  return PFD_SUBTYPE_TYPES.has(instrumentType) ? PFD_VALVE_SUBTYPE_OPTIONS : [];
}

// PFD instrument types that use an auto-generated tag (no manual entry). This is
// PFD-specific and intentionally separate from the shared generated_id_primary_types
// list — in P&ID these same types (FI/PI/TI/Control Valve, etc.) are manually tagged.
// The backend always computes instrument.generated_id, so the read-only display works
// without changing the shared identity rules.
const PFD_AUTO_TAG_TYPES = new Set(['Valve', 'Control Valve', 'Controller', 'Analyzer', 'FI', 'PI', 'TI', 'LI']);

const PROVENANCE_LABELS: Record<string, string> = {
  template_match: 'Template Match',
  manual: 'Manual',
  ai_extraction: 'AI Extraction',
  ml_detection: 'ML Detection',
  ai_identification: 'AI Identification',
};

function initSharedFormState(inst: PnidInstrument): SharedInstrumentEditorState {
  const isBranch = !!(inst.branch_ref || inst.branch_direction);
  return {
    mode: isBranch ? 'branch_join' : 'instrument',
    context_type: inst.context_type ?? null,
    context_id: inst.context_id ?? null,
    branch_ref: inst.branch_ref ?? '',
    branch_direction: inst.branch_direction ?? '',
    present_on_node: inst.present_on_node ?? true,
    // The stored owner, not the drawing's: empty is what the dirty diff reads as
    // "the reviewer named no owner", which is the drawing that draws it.
    owner_domain_id: inst.owner_domain_id ?? '',
    description: inst.description ?? '',
  };
}

function buildCoreFieldResetPatch(coreFields: Record<string, InstrumentCoreFieldValue>): Record<string, InstrumentCoreFieldValue> {
  return {
    ...coreFields,
    instrument_type: null,
    subtype: null,
    contextual_type: null,
    isolation_valve_tags: [],
  };
}

function makeDraftRowId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function getInitialLinkedInstrumentRows(instrument: PnidInstrument): LinkedInstrumentDraftRow[] {
  return (instrument.linked_instruments ?? []).map((link) => ({
    row_id: makeDraftRowId(),
    instrument_id: link.instrument_id ?? '',
    instrument_ref: link.instrument_ref ?? '',
    connection_type: link.connection_type,
  }));
}

function normalizeLinkedInstrumentRows(rows: LinkedInstrumentDraftRow[]): PnidLinkedInstrumentLink[] {
  return rows.map((row) => ({
    instrument_id: row.instrument_id || null,
    instrument_ref: row.instrument_ref.trim(),
    connection_type: row.connection_type as LinkedInstrumentConnectionType,
  }));
}

function areLinkedInstrumentListsEqual(a: PnidLinkedInstrumentLink[], b: PnidLinkedInstrumentLink[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function arePnidHyperlinksEqual(a: PnidHyperlinkRef | null, b: PnidHyperlinkRef | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.pnid_doc_id === b.pnid_doc_id &&
    a.pnid_no === b.pnid_no &&
    (a.source_opc_instrument_id ?? null) === (b.source_opc_instrument_id ?? null) &&
    (a.source_opc_tag ?? null) === (b.source_opc_tag ?? null) &&
    (a.target_node_id ?? null) === (b.target_node_id ?? null) &&
    (a.target_opc_instrument_id ?? null) === (b.target_opc_instrument_id ?? null) &&
    (a.target_opc_tag ?? null) === (b.target_opc_tag ?? null) &&
    (a.target_line_no_snapshot ?? null) === (b.target_line_no_snapshot ?? null) &&
    (a.status ?? 'confirmed') === (b.status ?? 'confirmed') &&
    (a.source ?? 'manual') === (b.source ?? 'manual')
  );
}

function withSourceOpcSnapshot(
  link: PnidHyperlinkRef | null,
  instrument: PnidInstrument,
  sourceTag: string | null,
): PnidHyperlinkRef | null {
  if (!link) return null;
  return {
    ...link,
    source_opc_instrument_id: instrument.id,
    source_opc_tag: sourceTag,
  };
}

function getSourceOpcTag(instrument: PnidInstrument, nextInstrumentText?: unknown): string | null {
  const editedTag = typeof nextInstrumentText === 'string' ? nextInstrumentText.trim() : '';
  if (editedTag) return editedTag;
  const currentTag = getInstrumentPrimaryLabel(instrument).trim();
  return currentTag || null;
}

function isOpcInstrumentType(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toUpperCase() === 'OPC';
}

function formatStoredValue(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return '-';
}

function formatLinkStatus(value: unknown): string {
  return value === 'needs_review' ? 'Needs review' : 'Confirmed';
}

function getNodeOpcTag(instruments: PnidInstrument[], nodeId: string | null | undefined): string | null {
  if (!nodeId) return null;
  const seen = new Set<string>();
  const candidates = instruments
    .filter((inst) => {
      if (!inst.id || seen.has(inst.id) || !isOpcInstrumentType(inst.instrument_type)) return false;
      seen.add(inst.id);
      const effectiveContext = getInstrumentEffectiveContext(inst, { instruments });
      return effectiveContext.context_type === 'node' && effectiveContext.context_id === nodeId;
    })
    .sort((a, b) => getInstrumentPrimaryLabel(a).localeCompare(getInstrumentPrimaryLabel(b)));
  const selected = candidates[0];
  return selected ? getInstrumentPrimaryLabel(selected) || selected.generated_id || null : null;
}

function computeUpdates(
  original: PnidInstrument,
  shared: SharedInstrumentEditorState,
  core: InstrumentCoreFields,
  linkedInstrumentRows: LinkedInstrumentDraftRow[],
  pnidHyperlinkDraft: PnidHyperlinkRef | null,
  tagStatus: InstrumentTagStatus,
): PnidInstrumentUpdate {
  const updates: PnidInstrumentUpdate = {};

  if ((shared.context_type ?? null) !== (original.context_type ?? null)) {
    updates.context_type = shared.context_type;
  }
  if ((shared.context_id ?? null) !== (original.context_id ?? null)) {
    updates.context_id = shared.context_id;
  }
  if ((shared.branch_ref || null) !== (original.branch_ref || null)) {
    updates.branch_ref = shared.branch_ref || null;
  }
  if ((shared.branch_direction || null) !== (original.branch_direction || null)) {
    updates.branch_direction = (shared.branch_direction as BranchDirection) || null;
  }
  if (shared.present_on_node !== (original.present_on_node ?? true)) {
    updates.present_on_node = shared.present_on_node;
  }
  // Ownership is a fact about the asset, not an engineering attribute, so it is
  // a top-level field rather than a `core_fields` entry. It is diffed like every
  // other field here, so a save that changed nothing still sends nothing.
  if (shared.owner_domain_id !== (original.owner_domain_id ?? '')) {
    updates.owner_domain_id = shared.owner_domain_id;
  }
  // A description, like ownership, is a top-level field. A blank one is sent as
  // null, which is how the update clears it.
  const nextDescription = shared.description.trim() || null;
  if (nextDescription !== (original.description || null)) {
    updates.description = nextDescription;
  }

  if (haveCoreFormValuesChanged(original, core)) {
    Object.assign(updates, buildCoreInstrumentUpdates(core));
  }
  if (tagStatus !== original.tag_status) {
    updates.tag_status = tagStatus;
  }

  const nextLinkedInstruments = shared.mode === 'branch_join' ? [] : normalizeLinkedInstrumentRows(linkedInstrumentRows);
  const originalLinkedInstruments = original.linked_instruments ?? [];
  if (!areLinkedInstrumentListsEqual(nextLinkedInstruments, originalLinkedInstruments)) {
    updates.linked_instruments = nextLinkedInstruments;
  }

  const effectiveType =
    typeof core.instrument_type === 'string' && core.instrument_type
      ? core.instrument_type
      : (original.instrument_type ?? '');
  const originalHyperlink = original.pnid_hyperlink ?? null;
  const nextHyperlink =
    shared.mode === 'instrument' && isOpcInstrumentType(effectiveType)
      ? withSourceOpcSnapshot(pnidHyperlinkDraft, original, getSourceOpcTag(original, core.instrument_text))
      : null;
  if (!arePnidHyperlinksEqual(nextHyperlink, originalHyperlink)) {
    updates.pnid_hyperlink = nextHyperlink;
  }

  if (shared.mode === 'branch_join') {
    const currentCoreFields = updates.core_fields ?? buildCoreInstrumentUpdates(core).core_fields ?? {};
    updates.core_fields = buildCoreFieldResetPatch(currentCoreFields);

    if (original.instrument_type) updates.instrument_type = null;
    if (original.subtype) updates.subtype = null;
    if (original.contextual_type) updates.contextual_type = null;
    if (original.isolation_valve_tags?.length) updates.isolation_valve_tags = [];
    if (original.linked_instruments?.length) updates.linked_instruments = [];
  } else if (shared.mode === 'instrument') {
    if (original.branch_ref) updates.branch_ref = null;
    if (original.branch_direction) updates.branch_direction = null;
  }

  return updates;
}

function getInitialPfdValues(instrument: PnidInstrument): Record<string, any> {
  const core = instrument.core_fields ?? {};
  return {
    ...core,
    instrument_type: instrument.instrument_type ?? core.instrument_type ?? '',
    instrument_text: instrument.instrument_text ?? core.instrument_text ?? '',
    subtype: instrument.subtype ?? core.subtype ?? '',
    notes: instrument.notes ?? core.notes ?? '',
    from_osbl: core.from_osbl ?? '',
    to_osbl: core.to_osbl ?? '',
    controlled_variable: core.controlled_variable ?? '',
    components_analyzed: core.components_analyzed ?? '',
    // Read-only display only; not a schema field, so it is ignored on save.
    generated_id: instrument.generated_id ?? '',
  };
}

function buildPfdInstrumentUpdates(
  original: PnidInstrument,
  values: Record<string, any>,
  schema: FormSchemaDefinition | null,
): PnidInstrumentUpdate {
  const updates: PnidInstrumentUpdate = {};
  const nextCoreFields: Record<string, InstrumentCoreFieldValue> = { ...(original.core_fields ?? {}) };
  const fieldKeys = schema?.sections.flatMap((section) => section.fields.map((field) => field.key)) ?? [
    'instrument_type',
    'instrument_text',
    'notes',
  ];

  // pnid_hyperlink is a schema field for layout/visibility only; its value is
  // managed by pnidHyperlinkDraft and persisted separately in handleSave.
  const TOP_LEVEL_KEYS = new Set(['instrument_type', 'instrument_text', 'subtype', 'notes']);
  for (const key of fieldKeys) {
    if (key === 'pnid_hyperlink') continue;
    if (!(key in values)) continue;
    const value = values[key] as InstrumentCoreFieldValue;
    if (TOP_LEVEL_KEYS.has(key)) {
      (updates as Record<string, unknown>)[key] = value === '' ? null : value;
    }
    nextCoreFields[key] = value;
  }

  // Clear fields that don't apply to the selected type so a stale value (e.g. left
  // over after switching type) can't leak into persistence, exports, or labels.
  const type = typeof values.instrument_type === 'string' ? values.instrument_type : '';
  if (!PFD_SUBTYPE_TYPES.has(type)) {
    updates.subtype = null;
    nextCoreFields.subtype = null;
  }
  if (type !== 'Controller') nextCoreFields.controlled_variable = null;
  if (type !== 'Analyzer') nextCoreFields.components_analyzed = null;
  if (type !== 'OPC') {
    nextCoreFields.from_osbl = null;
    nextCoreFields.to_osbl = null;
  }

  updates.core_fields = nextCoreFields;
  return updates;
}

const InstrumentEditor: React.FC<InstrumentEditorProps> = ({
  instrument,
  position,
  onSave,
  onDelete,
  onClose,
  allNodes,
  allEquipments,
  allInstruments,
  onHighlightEntity,
  onOpenPnidHyperlink,
  allDocuments,
  currentDocId,
  currentDomainId,
  currentPlant,
  diagramMode = 'pid',
  linkedDocumentStates = {},
  onLoadLinkedDocumentState,
  onOpenLinkedDocument,
  docInstruments,
  onMarkReviewed,
  readOnly = false,
  latestInstrumentLoading = false,
  latestInstrumentError = null,
  identifyPrefill = null,
  onEditRegions,
}) => {
  const [shared, setShared] = useState<SharedInstrumentEditorState>(() => initSharedFormState(instrument));
  const [coreForm, dispatchCoreForm] = useReducer(coreFormReducer, undefined, () =>
    initialCoreFormState(instrument, identifyPrefill ?? {}),
  );
  const core = coreForm.values;
  const [linkedInstrumentRows, setLinkedInstrumentRows] = useState<LinkedInstrumentDraftRow[]>(() => getInitialLinkedInstrumentRows(instrument));
  const [pnidHyperlinkDraft, setPnidHyperlinkDraft] = useState<PnidHyperlinkRef | null>(instrument.pnid_hyperlink ?? null);
  const [linkedPnidState, setLinkedPnidState] = useState<PnidWizardState | null>(null);
  const [linkedPnidStateDocId, setLinkedPnidStateDocId] = useState<string | null>(null);
  const [linkedPnidLoading, setLinkedPnidLoading] = useState(false);
  const [linkedPnidError, setLinkedPnidError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [provenanceExpanded, setProvenanceExpanded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tagStatus, setTagStatus] = useState<InstrumentTagStatus>(instrument.tag_status ?? 'untagged');
  const isPfdMode = diagramMode === 'pfd';
  const effectiveEditorMode = isPfdMode ? 'instrument' : shared.mode;
  // The account this drawing belongs to is what scopes the Domain picker, and the
  // drawing's own plant owns an instrument that names no owner.
  const drawingDomainId = currentDomainId ?? '';
  const pfdInstrumentSchema = useFormSchema(isPfdMode ? 'pfd_instrument' : null);
  const [pfdValues, setPfdValues] = useState<Record<string, any>>(() => getInitialPfdValues(instrument));

  // Read by the identify effect, which must not restart on a new object identity.
  const coreFormRef = useRef(coreForm);
  coreFormRef.current = coreForm;
  const identifyPrefillRef = useRef(identifyPrefill);
  identifyPrefillRef.current = identifyPrefill;
  const identifyAbortRef = useRef<AbortController | null>(null);
  /**
   * Counts hand edits of the tag, against the instrument they were made on. Only a
   * hand edit advances it, so neither an identify answer nor the reset below reads as
   * one, and a repeat of the same text still restarts the debounce.
   */
  const [tagEdit, setTagEdit] = useState<{ instrumentId: string; count: number } | null>(null);

  useEffect(() => {
    setShared(initSharedFormState(instrument));
    dispatchCoreForm({
      type: 'reset',
      instrument,
      identifiedFields: identifyPrefillRef.current ?? {},
    });
    // A new instrument carries none of the last one's edits, and the answer still in
    // flight is about the last one's box.
    setTagEdit(null);
    identifyAbortRef.current?.abort();
    setPfdValues(getInitialPfdValues(instrument));
    setLinkedInstrumentRows(getInitialLinkedInstrumentRows(instrument));
    setPnidHyperlinkDraft(instrument.pnid_hyperlink ?? null);
    setTagStatus(instrument.tag_status ?? 'untagged');
    setDeleteConfirm(false);
    setSaveError(null);
  }, [instrument.id]);

  const reviewTarget = useMemo(
    () => getInstrumentReviewTarget(instrument, docInstruments),
    [instrument, docInstruments],
  );
  const isReviewed = instrument.status === 'final_and_reviewed';
  const latestInstrumentLocked = latestInstrumentLoading || Boolean(latestInstrumentError);

  const handleMarkReviewedClick = useCallback(async () => {
    if (!onMarkReviewed || !reviewTarget || isReviewed || reviewSaving || saving || latestInstrumentLocked) return;
    try {
      setReviewSaving(true);
      await onMarkReviewed(reviewTarget);
    } finally {
      setReviewSaving(false);
    }
  }, [onMarkReviewed, reviewTarget, isReviewed, reviewSaving, saving, latestInstrumentLocked]);

  const setSharedField = useCallback(<K extends keyof SharedInstrumentEditorState>(key: K, value: SharedInstrumentEditorState[K]) => {
    setShared((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Every hand edit of the core form, from one field or from a control that sets several. */
  const applySmeCoreEdit = useCallback((patch: InstrumentCoreFields) => {
    dispatchCoreForm({ type: 'sme_edit', patch });
    if ('instrument_text' in patch) {
      setTagEdit((prev) => ({
        instrumentId: instrument.id,
        count: prev && prev.instrumentId === instrument.id ? prev.count + 1 : 1,
      }));
    }
  }, [instrument.id]);

  const setCoreField = useCallback((key: string, value: InstrumentCoreFieldValue) => {
    applySmeCoreEdit({ [key]: Array.isArray(value) ? [...value] : value });
  }, [applySmeCoreEdit]);

  const setPfdField = useCallback((key: string, value: any) => {
    setPfdValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Clear a stale subtype when the PFD instrument type changes to one whose
  // allowed subtypes no longer include the current value (e.g. Valve -> OPC).
  useEffect(() => {
    if (!isPfdMode) return;
    const type = typeof pfdValues.instrument_type === 'string' ? pfdValues.instrument_type : '';
    const validSubtypes = pfdSubtypeOptions(type);
    if (pfdValues.subtype && !validSubtypes.includes(pfdValues.subtype)) {
      setPfdField('subtype', '');
    }
  }, [isPfdMode, pfdValues.instrument_type, pfdValues.subtype, setPfdField]);

  const selectableInstrumentEntries = useMemo(() => {
    return allInstruments.filter((entry) => entry.id !== instrument.id && (entry.instrument_text || entry.generated_id));
  }, [allInstruments, instrument.id]);

  const isolationValveTagCandidates = useMemo(() => {
    const seen = new Set<string>();
    const candidates: string[] = [];
    for (const entry of selectableInstrumentEntries) {
      const label = formatInstrumentIdentity(entry).trim();
      if (!label || seen.has(label)) {
        continue;
      }
      seen.add(label);
      candidates.push(label);
    }
    return candidates;
  }, [selectableInstrumentEntries]);

  const isolationValveTagCandidateIds = useMemo(() => {
    const ids = new Map<string, string>();
    for (const entry of selectableInstrumentEntries) {
      const label = formatInstrumentIdentity(entry).trim();
      if (!label || ids.has(label)) {
        continue;
      }
      ids.set(label, entry.id);
    }
    return ids;
  }, [selectableInstrumentEntries]);

  const actingOnCandidates = useMemo(() => {
    const dedupe = (values: Array<string | null | undefined>) =>
      Array.from(new Set(values.map((value) => (value ?? '').trim()).filter(Boolean)));
    return {
      equipment: dedupe(allEquipments.map((eq) => eq.equipment_no)),
      instrument: isolationValveTagCandidates,
    };
  }, [allEquipments, isolationValveTagCandidates]);

  useEffect(() => {
    const sanitized = sanitizeCoreFormState(
      core,
      shared,
      isolationValveTagCandidates,
      actingOnCandidates,
      coreForm.touched,
    );
    if (JSON.stringify(sanitized) !== JSON.stringify(core)) {
      dispatchCoreForm({ type: 'sanitized', values: sanitized });
    }
  }, [core, coreForm.touched, shared, isolationValveTagCandidates, actingOnCandidates]);

  // The tag drives every rule the backend applies, so a hand-typed tag is worth
  // another identify. No other field is, which is why this watches only `tagEdit`.
  // The regions, serialized. A fresh array changes identity on every render and would
  // restart the debounce each time, so the effect keys on the geometry's own text and
  // reads the regions back out of it.
  const identifyRegionsKey = JSON.stringify(instrumentRegions(instrument));
  useEffect(() => {
    if (!tagEdit || tagEdit.instrumentId !== instrument.id) return;
    const regions = JSON.parse(identifyRegionsKey) as BoundingBox[];
    // Absent when an existing instrument was opened, which the SME did not draw.
    if (!identifyPrefillRef.current || !currentDocId || regions.length === 0 || readOnly) return;

    const debounce = setTimeout(() => {
      const controller = new AbortController();
      identifyAbortRef.current = controller;
      const { values, touched } = coreFormRef.current;
      assetApi
        .identify(
          currentDocId,
          {
            // Read live, so a region the SME resized or added after drawing is read.
            bboxes: regions,
            existing_fields: values,
            touched_fields: Array.from(touched),
          },
          { signal: controller.signal },
        )
        .then((response) => {
          if (controller.signal.aborted) return;
          dispatchCoreForm({ type: 'identified', fields: response.fields });
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.warn('Re-identify after a tag edit failed; the form keeps its values:', error);
        });
    }, IDENTIFY_DEBOUNCE_MS);

    // A further edit, or the dialog closing, drops both the pending call and the
    // one already in flight — its answer is about a tag the SME has moved past.
    return () => {
      clearTimeout(debounce);
      identifyAbortRef.current?.abort();
    };
  }, [tagEdit, instrument.id, identifyRegionsKey, currentDocId, readOnly]);

  const nodeOptions = useMemo(() => {
    return allNodes.map((n) => ({
      value: n.id,
      label: `#${n.node_no} — ${n.line_no || 'N/A'}`,
    }));
  }, [allNodes]);

  const equipmentOptions = useMemo(() => {
    return allEquipments.map((eq) => ({
      value: eq.id,
      label: eq.equipment_no || 'Unnamed equipment',
    }));
  }, [allEquipments]);

  const instrumentContextOptions = useMemo(() => {
    return selectableInstrumentEntries
      .map((i) => ({
        value: i.id,
        label: formatInstrumentIdentity(i),
      }));
  }, [selectableInstrumentEntries]);

  const branchNodeOptions = useMemo(() => {
    return allNodes
      .filter((n) => n.id !== shared.context_id)
      .map((n) => ({
        value: String(n.node_no),
        label: `#${n.node_no} — ${n.line_no || 'N/A'}`,
      }));
  }, [allNodes, shared.context_id]);

  const effectiveInstrumentContext = useMemo(() => {
    return getInstrumentEffectiveContext(
      {
        ...instrument,
        context_type: shared.context_type,
        context_id: shared.context_id,
      },
      { instruments: docInstruments ?? [] },
    );
  }, [docInstruments, instrument, shared.context_id, shared.context_type]);

  const attachedStreamNode = useMemo(() => {
    if (effectiveInstrumentContext.context_type !== 'node' || !effectiveInstrumentContext.context_id) {
      return null;
    }
    return allNodes.find((node) => node.id === effectiveInstrumentContext.context_id) ?? null;
  }, [allNodes, effectiveInstrumentContext]);

  const linkedPfdStreamRows = useMemo<PnidLinkedPfdStreamRef[]>(() => {
    if (!isPfdMode || !attachedStreamNode?.linked_pfd_streams) return [];
    return attachedStreamNode.linked_pfd_streams.filter((row) => row.pfd_file_id || row.node_id);
  }, [attachedStreamNode, isPfdMode]);

  useEffect(() => {
    if (!onLoadLinkedDocumentState) return;
    const docIds = new Set(
      linkedPfdStreamRows
        .map((row) => row.pfd_file_id)
        .filter((docId): docId is string => Boolean(docId)),
    );
    for (const docId of docIds) {
      if (!linkedDocumentStates[docId]) {
        void onLoadLinkedDocumentState(docId);
      }
    }
  }, [linkedDocumentStates, linkedPfdStreamRows, onLoadLinkedDocumentState]);

  const visibleCoreFields = useMemo(() => {
    const visible = CORE_FORM_FIELDS.filter((field) => isCoreFieldVisible(field, shared, core));
    // The tag is what the SME reads off the sheet first and what every rule keys on,
    // so it leads the form. The order is decided here rather than in
    // `instrument-schema.json` because that file is one artefact shared with the
    // backend, which loads the same field list at import; a reader's field order is
    // this form's business, not the contract's.
    const tagIndex = visible.findIndex((field) => field.storage_key === 'instrument_text');
    if (tagIndex <= 0) return visible;
    const reordered = [...visible];
    reordered.unshift(...reordered.splice(tagIndex, 1));
    return reordered;
  }, [shared, core]);

  const linkedInstrumentOptions = useMemo(() => {
    return selectableInstrumentEntries
      .map((entry) => ({
        value: entry.id,
        label: formatInstrumentIdentity(entry),
      }));
  }, [selectableInstrumentEntries]);

  const linkedConnectionTypeOptions = useMemo(() => {
    return LINKED_INSTRUMENT_CONNECTION_TYPES.map((value) => ({ value, label: value }));
  }, []);

  const samePlantDocOptions = useMemo(() => {
    if (!currentPlant) return [];
    return (allDocuments ?? [])
      .filter((doc) => {
        const docType = doc.diagram_type === 'pfd' ? 'pfd' : 'pid';
        return docType === 'pid'
          && doc.plant === currentPlant
          && doc.id !== (currentDocId ?? null)
          && typeof doc.pnid_no === 'string'
          && doc.pnid_no.trim() !== '';
      })
      .map((doc) => {
        const pnidNo = (doc.pnid_no ?? '').trim();
        return { value: doc.id, label: pnidNo };
      });
  }, [allDocuments, currentPlant, currentDocId]);

  const linkedPnidOptions = useMemo(() => {
    const options = [...samePlantDocOptions];
    if (!pnidHyperlinkDraft?.pnid_doc_id) {
      return options;
    }

    if (options.some((option) => option.value === pnidHyperlinkDraft.pnid_doc_id)) {
      return options;
    }

    const matchedDoc = (allDocuments ?? []).find((doc) => doc.id === pnidHyperlinkDraft.pnid_doc_id);
    const fallbackPnidNo = (pnidHyperlinkDraft.pnid_no ?? '').trim();
    let fallbackLabel = fallbackPnidNo || pnidHyperlinkDraft.pnid_doc_id;
    if (matchedDoc) {
      const matchedPnidNo = (matchedDoc.pnid_no ?? '').trim();
      fallbackLabel = matchedPnidNo || matchedDoc.original_filename || matchedDoc.filename || fallbackLabel;
    }

    return [{ value: pnidHyperlinkDraft.pnid_doc_id, label: fallbackLabel }, ...options];
  }, [allDocuments, pnidHyperlinkDraft, samePlantDocOptions]);

  const linkedPnidStatusMessage = useMemo(() => {
    if (!pnidHyperlinkDraft?.pnid_doc_id) {
      return null;
    }
    if (samePlantDocOptions.some((option) => option.value === pnidHyperlinkDraft.pnid_doc_id)) {
      return null;
    }
    return pnidHyperlinkDraft.pnid_no
      ? `Stored link target unavailable in current same-plant list: ${pnidHyperlinkDraft.pnid_no}`
      : 'Stored link target unavailable in current same-plant list.';
  }, [pnidHyperlinkDraft, samePlantDocOptions]);

  const linkedPnidDocId = pnidHyperlinkDraft?.pnid_doc_id ?? null;

  useEffect(() => {
    if (!linkedPnidDocId) {
      setLinkedPnidState(null);
      setLinkedPnidStateDocId(null);
      setLinkedPnidLoading(false);
      setLinkedPnidError(null);
      return;
    }

    let cancelled = false;
    setLinkedPnidLoading(true);
    setLinkedPnidError(null);

    pnidApi.get(linkedPnidDocId)
      .then((state) => {
        if (cancelled) return;
        setLinkedPnidState(state);
        setLinkedPnidStateDocId(linkedPnidDocId);
      })
      .catch(() => {
        if (cancelled) return;
        setLinkedPnidState(null);
        setLinkedPnidStateDocId(null);
        setLinkedPnidError('Could not load target P&ID lines.');
      })
      .finally(() => {
        if (!cancelled) {
          setLinkedPnidLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [linkedPnidDocId]);

  const linkedPnidNodeOptions = useMemo(() => {
    const options = linkedPnidDocId && linkedPnidStateDocId === linkedPnidDocId && linkedPnidState
      ? [...linkedPnidState.nodes]
          .sort((a, b) => a.node_no - b.node_no)
          .map((node) => ({
            value: node.id,
            label: `#${node.node_no} - ${node.line_no || 'N/A'}`,
          }))
      : [];
    const storedNodeId = pnidHyperlinkDraft?.target_node_id ?? null;
    if (storedNodeId && !options.some((option) => option.value === storedNodeId)) {
      const storedLabel = pnidHyperlinkDraft?.target_line_no_snapshot
        ? `${pnidHyperlinkDraft.target_line_no_snapshot} (current)`
        : 'Selected target line';
      return [{ value: storedNodeId, label: storedLabel }, ...options];
    }
    return options;
  }, [linkedPnidDocId, linkedPnidState, linkedPnidStateDocId, pnidHyperlinkDraft]);

  const linkedPnidNodeById = useMemo(() => {
    return new Map((linkedPnidState?.nodes ?? []).map((node) => [node.id, node]));
  }, [linkedPnidState]);

  const linkedPnidOpcOptions = useMemo(() => {
    const options = linkedPnidDocId && linkedPnidStateDocId === linkedPnidDocId && linkedPnidState
      ? linkedPnidState.instruments
          .filter((inst) => isOpcInstrumentType(inst.instrument_type))
          .map((inst) => {
            const effectiveContext = getInstrumentEffectiveContext(inst, { instruments: linkedPnidState.instruments });
            const node = effectiveContext.context_type === 'node' && effectiveContext.context_id
              ? linkedPnidNodeById.get(effectiveContext.context_id)
              : null;
            const tag = getInstrumentPrimaryLabel(inst) || inst.id;
            return {
              value: inst.id,
              label: node?.line_no ? `${tag} - ${node.line_no}` : tag,
            };
          })
      : [];
    const storedOpcId = pnidHyperlinkDraft?.target_opc_instrument_id ?? null;
    if (storedOpcId && !options.some((option) => option.value === storedOpcId)) {
      const storedLabel = pnidHyperlinkDraft?.target_opc_tag
        ? `${pnidHyperlinkDraft.target_opc_tag} (current)`
        : 'Selected target OPC';
      return [{ value: storedOpcId, label: storedLabel }, ...options];
    }
    return options;
  }, [linkedPnidDocId, linkedPnidNodeById, linkedPnidState, linkedPnidStateDocId, pnidHyperlinkDraft]);

  const linkedPnidNodeStatusMessage = useMemo(() => {
    if (!pnidHyperlinkDraft?.pnid_doc_id) {
      return null;
    }
    if (linkedPnidLoading) {
      return 'Loading target lines...';
    }
    if (linkedPnidError) {
      return linkedPnidError;
    }
    if (!pnidHyperlinkDraft.target_node_id) {
      return linkedPnidNodeOptions.length === 0
        ? 'No target lines found in the linked P&ID.'
        : 'Target line is optional while annotating; select it when resolving the linked P&ID.';
    }
    if (linkedPnidNodeOptions.some((option) => option.value === pnidHyperlinkDraft.target_node_id)) {
      return null;
    }
    return `Stored target line id unavailable in linked P&ID: ${pnidHyperlinkDraft.target_node_id}`;
  }, [linkedPnidError, linkedPnidLoading, linkedPnidNodeOptions, pnidHyperlinkDraft]);

  const handleSave = async () => {
    if (!isPfdMode && effectiveEditorMode === 'instrument') {
      const normalizedRows = normalizeLinkedInstrumentRows(linkedInstrumentRows);
      const seen = new Set<string>();
      for (const row of normalizedRows) {
        if (!row.connection_type) {
          setSaveError('Each linked instrument row needs a connection type.');
          return;
        }
        if (!row.instrument_id && !row.instrument_ref) {
          setSaveError('Each linked instrument row needs an instrument.');
          return;
        }
        const dedupeKey = `${row.instrument_id ?? `ref:${row.instrument_ref.toLowerCase()}`}:${row.connection_type}`;
        if (seen.has(dedupeKey)) {
          setSaveError('Duplicate linked instrument rows are not allowed.');
          return;
        }
        seen.add(dedupeKey);
      }
    }

    const sharedForSave = isPfdMode ? { ...shared, mode: 'instrument' as const } : shared;
    let updates: PnidInstrumentUpdate;
    if (isPfdMode) {
      updates = {
        ...computeUpdates(instrument, sharedForSave, core, linkedInstrumentRows, pnidHyperlinkDraft, tagStatus),
        ...buildPfdInstrumentUpdates(instrument, pfdValues, pfdInstrumentSchema),
      };
      // The OPC hyperlink is keyed off the PFD-selected type (pfdValues), not the
      // PID core-form state that computeUpdates reads — recompute it here.
      const pfdType = typeof pfdValues.instrument_type === 'string' ? pfdValues.instrument_type : '';
      const nextHyperlink =
        isOpcInstrumentType(pfdType)
          ? withSourceOpcSnapshot(pnidHyperlinkDraft, instrument, getSourceOpcTag(instrument, pfdValues.instrument_text))
          : null;
      if (arePnidHyperlinksEqual(nextHyperlink, instrument.pnid_hyperlink ?? null)) {
        delete updates.pnid_hyperlink;
      } else {
        updates.pnid_hyperlink = nextHyperlink;
      }
    } else {
      updates = computeUpdates(instrument, sharedForSave, core, linkedInstrumentRows, pnidHyperlinkDraft, tagStatus);
    }
    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await onSave(instrument.id, updates);
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (typeof detail === 'string') {
        setSaveError(detail);
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        setSaveError(String((detail as { message?: unknown }).message || 'Could not save instrument.'));
      } else {
        setSaveError('Could not save instrument.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await onDelete(instrument.id);
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (typeof detail === 'string') {
        setSaveError(detail);
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        setSaveError(String((detail as { message?: unknown }).message || 'Could not delete instrument.'));
      } else {
        setSaveError('Could not delete instrument.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Picking a mode is a hand edit like any other: the type it defaults to is the
  // SME's answer, and a later identify answer may not take it back.
  const handleSelectInstrumentMode = () => {
    setShared((prev) => ({
      ...prev,
      mode: 'instrument',
      branch_ref: '',
      branch_direction: '',
    }));
    if (!(typeof core.instrument_type === 'string' && core.instrument_type)) {
      applySmeCoreEdit({ instrument_type: 'Valve' });
    }
  };

  const handleSelectBranchMode = () => {
    setShared((prev) => ({
      ...prev,
      mode: 'branch_join',
      branch_direction: prev.branch_direction || 'OUT',
    }));
    applySmeCoreEdit({
      subtype: '',
      contextual_type: '',
      isolation_valve_tags: [],
    });
  };

  const renderDescriptionField = () => (
    <div style={{ fontSize: 12 }}>
      <label
        htmlFor={DESCRIPTION_INPUT_ID}
        style={{ display: 'block', fontWeight: 500, marginBottom: 4, color: 'var(--raven-text-primary)' }}
      >
        Description
      </label>
      <input
        id={DESCRIPTION_INPUT_ID}
        value={shared.description}
        onChange={(e) => setSharedField('description', e.target.value)}
        disabled={readOnly}
        style={{
          width: '100%',
          padding: 8,
          border: '1px solid var(--raven-divider)',
          borderRadius: 6,
          fontSize: 12,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );

  const renderCoreField = (field: CoreFieldDefinition) => {
    const value = core[field.storage_key];
    const options = buildCoreFieldOptions(field, shared, core, isolationValveTagCandidates, actingOnCandidates);
    const currentInstrumentType =
      typeof core.instrument_type === 'string' && core.instrument_type
        ? core.instrument_type
        : (instrument.instrument_type ?? '');
    const isGeneratedIdTagField =
      field.storage_key === 'instrument_text' &&
      shared.mode === 'instrument' &&
      isGeneratedIdPrimaryInstrumentType(currentInstrumentType);

    if (field.widget === 'textarea') {
      return (
        <div key={field.id} style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{field.label}</div>
          <textarea
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => setCoreField(field.storage_key, e.target.value)}
            placeholder={field.placeholder}
            rows={2}
            readOnly={readOnly}
            style={{
              width: '100%',
              padding: 8,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              fontSize: 12,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          {field.storage_key === 'notes' && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
              Attached to {getInstrumentPrimaryLabel(instrument) || instrument.generated_id || 'this instrument'}
            </div>
          )}
        </div>
      );
    }

    if (field.widget === 'text') {
      if (isGeneratedIdTagField) {
        const displayValue = typeof value === 'string' ? value : '';
        return (
          <div key={field.id} style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{field.label}</div>
            <input
              value={displayValue}
              onChange={(e) => setCoreField(field.storage_key, e.target.value)}
              placeholder={generatedId || 'Generated on save'}
              readOnly={readOnly}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
              Generated fallback: {generatedId || 'available after save'}
            </div>
          </div>
        );
      }

      if (field.storage_key === 'instrument_text') {
        return (
          <div key={field.id} style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{field.label}</div>
            <TagAutocompleteInput
              value={typeof value === 'string' ? value : ''}
              onChange={(next) => setCoreField(field.storage_key, next)}
              docId={currentDocId}
              kind="instrument"
              placeholder={field.placeholder}
              disabled={readOnly}
            />
          </div>
        );
      }

      return (
        <div key={field.id} style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{field.label}</div>
          <input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => setCoreField(field.storage_key, e.target.value)}
            placeholder={field.placeholder}
            readOnly={readOnly}
            style={{
              width: '100%',
              padding: 8,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              fontSize: 12,
              boxSizing: 'border-box',
            }}
          />
        </div>
      );
    }

    if (field.widget === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      const availableOptions = options.filter((option) => !selectedValues.includes(option.value));
      return (
        <div key={field.id} style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            {field.label}
            {selectedValues.length > 0 && (
              <span style={{ fontWeight: 400, color: '#059669', marginLeft: 8 }}>
                {selectedValues.length} selected
              </span>
            )}
          </div>
          {selectedValues.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              {selectedValues.map((selectedValue) => (
                <span
                  key={selectedValue}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    background: '#dcfce7',
                    border: '1px solid #86efac',
                    borderRadius: 12,
                    fontSize: 11,
                  }}
                >
                  {selectedValue}
                  <button
                    onClick={() => setCoreField(field.storage_key, selectedValues.filter((entry) => entry !== selectedValue))}
                    disabled={readOnly}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      fontSize: 12,
                      color: '#6b7280',
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
          <AutocompleteDropdown
            value=""
            onChange={(nextValue) => {
              if (!nextValue || selectedValues.includes(nextValue)) {
                return;
              }
              setCoreField(field.storage_key, [...selectedValues, nextValue]);
            }}
            options={availableOptions}
            placeholder={field.placeholder}
            allowCustom={field.allow_custom}
            disabled={readOnly}
            onHoverOption={
              field.storage_key === 'isolation_valve_tags'
                ? (nextValue) => onHighlightEntity?.(nextValue ? isolationValveTagCandidateIds.get(nextValue) ?? null : null)
                : undefined
            }
          />
        </div>
      );
    }

    if (field.widget === 'checkbox') {
      return (
        <div key={field.id} style={{ fontSize: 12 }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 500,
              color: '#111827',
              cursor: readOnly ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={value === true}
              onChange={(e) => setCoreField(field.storage_key, e.target.checked)}
              disabled={readOnly}
              style={{ accentColor: '#3b82f6' }}
            />
            <span>{field.label}</span>
          </label>
        </div>
      );
    }

    return (
      <div key={field.id} style={{ fontSize: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>{field.label}</div>
        <AutocompleteDropdown
          value={typeof value === 'string' ? value : ''}
          onChange={(nextValue) => setCoreField(field.storage_key, nextValue)}
          options={options}
          placeholder={field.placeholder}
          allowCustom={field.allow_custom}
          disabled={readOnly}
        />
      </div>
    );
  };

  const updateLinkedInstrumentRow = useCallback(
    (rowId: string, patch: Partial<LinkedInstrumentDraftRow>) => {
      setLinkedInstrumentRows((prev) =>
        prev.map((row) => {
          if (row.row_id !== rowId) {
            return row;
          }
          return { ...row, ...patch };
        })
      );
    },
    [],
  );

  const removeLinkedInstrumentRow = useCallback((rowId: string) => {
    setLinkedInstrumentRows((prev) => prev.filter((row) => row.row_id !== rowId));
  }, []);

  const addLinkedInstrumentRow = useCallback(() => {
    setLinkedInstrumentRows((prev) => [
      ...prev,
      {
        row_id: makeDraftRowId(),
        instrument_id: '',
        instrument_ref: '',
        connection_type: 'Connected to Process',
      },
    ]);
  }, []);

  const renderLinkedInstrumentSection = () => (
    <div
      style={{
        padding: 10,
        background: '#f9fafb',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Linked Instruments</div>
        <button
          type="button"
          onClick={addLinkedInstrumentRow}
          disabled={readOnly}
          style={{
            padding: '4px 8px',
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            background: '#fff',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          Add Link
        </button>
      </div>
      {linkedInstrumentRows.length === 0 ? (
        <div style={{ fontSize: 11, color: '#6b7280' }}>No linked instruments.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {linkedInstrumentRows.map((row) => (
            <div
              key={row.row_id}
              style={{
                padding: 8,
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                background: '#fff',
              }}
            >
              <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 500, color: '#374151' }}>Instrument</div>
              <AutocompleteDropdown
                value={row.instrument_id}
                onChange={(nextValue) => {
                  const selected = linkedInstrumentOptions.find((option) => option.value === nextValue);
                  updateLinkedInstrumentRow(row.row_id, {
                    instrument_id: nextValue,
                    instrument_ref: selected ? selected.label : '',
                  });
                }}
                options={linkedInstrumentOptions}
                placeholder="Select linked instrument..."
                allowCustom={false}
                onHoverOption={onHighlightEntity}
                disabled={readOnly}
              />
              {!row.instrument_id && row.instrument_ref && (
                <div style={{ marginTop: 4, fontSize: 10, color: '#92400e' }}>
                  Legacy unresolved link: {row.instrument_ref}
                </div>
              )}
              <div style={{ marginTop: 8, marginBottom: 6, fontSize: 11, fontWeight: 500, color: '#374151' }}>
                Connection Type
              </div>
              <AutocompleteDropdown
                value={row.connection_type}
                onChange={(nextValue) => {
                  updateLinkedInstrumentRow(row.row_id, {
                    connection_type: nextValue as LinkedInstrumentConnectionType,
                  });
                }}
                options={linkedConnectionTypeOptions}
                placeholder="Select connection type..."
                allowCustom={false}
                disabled={readOnly}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => removeLinkedInstrumentRow(row.row_id)}
                  disabled={readOnly}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: '1px solid #fecaca',
                    background: '#fff',
                    color: '#b91c1c',
                    cursor: readOnly ? 'not-allowed' : 'pointer',
                    fontSize: 11,
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLinkedPnidSection = () => (
    <div
      style={{
        padding: 10,
        background: '#f9fafb',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Linked P&amp;ID</div>
        {pnidHyperlinkDraft && (
          <div style={{ display: 'flex', gap: 6 }}>
            {pnidHyperlinkDraft.pnid_doc_id && onOpenPnidHyperlink && (
              <button
                type="button"
                onClick={() => onOpenPnidHyperlink(pnidHyperlinkDraft, { newTab: true })}
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid #bfdbfe',
                  background: '#fff',
                  color: '#1d4ed8',
                  cursor: 'pointer',
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                }}
              >
                Open in new tab
              </button>
            )}
            <button
              type="button"
              onClick={() => setPnidHyperlinkDraft(null)}
              disabled={readOnly}
              style={{
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #fecaca',
                background: '#fff',
                color: '#b91c1c',
                cursor: readOnly ? 'not-allowed' : 'pointer',
                fontSize: 11,
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>
      {!currentPlant && !pnidHyperlinkDraft ? (
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          Current document has no plant set; hyperlink targets unavailable.
        </div>
      ) : linkedPnidOptions.length === 0 ? (
        <div style={{ fontSize: 11, color: '#6b7280' }}>No other P&amp;IDs in this plant.</div>
      ) : (
        <>
          <AutocompleteDropdown
            value={pnidHyperlinkDraft?.pnid_doc_id ?? ''}
            onChange={(nextValue) => {
              if (!nextValue) {
                setPnidHyperlinkDraft(null);
                return;
              }
              const selected = (allDocuments ?? []).find((doc) => doc.id === nextValue);
              if (!selected) return;
              setPnidHyperlinkDraft({
                pnid_doc_id: selected.id,
                pnid_no: (selected.pnid_no ?? '').trim(),
                source_opc_instrument_id: instrument.id,
                source_opc_tag: getSourceOpcTag(instrument, isPfdMode ? pfdValues.instrument_text : core.instrument_text),
                target_node_id: null,
                target_opc_instrument_id: null,
                target_opc_tag: null,
                target_line_no_snapshot: null,
                status: 'confirmed',
                source: 'manual',
              });
            }}
            options={linkedPnidOptions}
            placeholder="Select linked P&ID..."
            allowCustom={false}
            disabled={readOnly}
          />
          {linkedPnidStatusMessage && (
            <div style={{ marginTop: 4, fontSize: 10, color: '#92400e' }}>
              {linkedPnidStatusMessage}
            </div>
          )}
          {pnidHyperlinkDraft && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                background: '#fff',
                fontSize: 11,
                color: '#6b7280',
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 6,
                overflowWrap: 'anywhere',
              }}
            >
              <div>
                <strong>Source OPC:</strong> {formatStoredValue(pnidHyperlinkDraft.source_opc_tag)}
              </div>
              <div>
                <strong>Target P&amp;ID:</strong>{' '}
                {formatStoredValue(
                  linkedPnidOptions.find((option) => option.value === pnidHyperlinkDraft.pnid_doc_id)?.label
                    ?? pnidHyperlinkDraft.pnid_no
                    ?? pnidHyperlinkDraft.pnid_doc_id,
                )}
              </div>
              <div>
                <strong>Target OPC:</strong> {formatStoredValue(pnidHyperlinkDraft.target_opc_tag)}
              </div>
              <div>
                <strong>Target line:</strong> {formatStoredValue(pnidHyperlinkDraft.target_line_no_snapshot)}
              </div>
              <div>
                <strong>Status:</strong> {formatLinkStatus(pnidHyperlinkDraft.status)}
              </div>
            </div>
          )}
          {pnidHyperlinkDraft && !pnidHyperlinkDraft.pnid_doc_id && pnidHyperlinkDraft.pnid_no && (
            <div style={{ marginTop: 4, fontSize: 10, color: '#92400e' }}>
              Linked P&amp;ID needs refresh: {pnidHyperlinkDraft.pnid_no}
            </div>
          )}
          {pnidHyperlinkDraft?.pnid_doc_id && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                  Target OPC tag
                </div>
                <AutocompleteDropdown
                  value={pnidHyperlinkDraft.target_opc_instrument_id ?? ''}
                  onChange={(nextValue) => {
                    const selectedOpc = linkedPnidState?.instruments.find((inst) => inst.id === nextValue);
                    const effectiveContext = selectedOpc
                      ? getInstrumentEffectiveContext(selectedOpc, { instruments: linkedPnidState?.instruments ?? [] })
                      : null;
                    const targetNode = effectiveContext?.context_type === 'node' && effectiveContext.context_id
                      ? linkedPnidNodeById.get(effectiveContext.context_id)
                      : null;
                    setPnidHyperlinkDraft((prev) => prev ? {
                      ...prev,
                      target_opc_instrument_id: nextValue || null,
                      target_opc_tag: selectedOpc ? getInstrumentPrimaryLabel(selectedOpc) || null : null,
                      target_node_id: targetNode?.id ?? prev.target_node_id ?? null,
                      target_line_no_snapshot: targetNode?.line_no ?? prev.target_line_no_snapshot ?? null,
                    } : prev);
                  }}
                  options={linkedPnidOpcOptions}
                  placeholder={linkedPnidLoading ? 'Loading target OPCs...' : 'Select target OPC...'}
                  allowCustom={false}
                  disabled={readOnly || linkedPnidLoading || Boolean(linkedPnidError)}
                />
                {pnidHyperlinkDraft.target_opc_tag && (
                  <div style={{ marginTop: 4, fontSize: 10, color: '#6b7280' }}>
                    Selected target OPC: {pnidHyperlinkDraft.target_opc_tag}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                  Target line (optional)
                </div>
                <AutocompleteDropdown
                  value={pnidHyperlinkDraft.target_node_id ?? ''}
                  onChange={(nextValue) => {
                    const selectedNode = linkedPnidState?.nodes.find((node) => node.id === nextValue);
                    setPnidHyperlinkDraft((prev) => {
                      if (!prev) return prev;
                      const selectedOpc = linkedPnidState?.instruments.find((inst) => inst.id === prev.target_opc_instrument_id);
                      const effectiveContext = selectedOpc
                        ? getInstrumentEffectiveContext(selectedOpc, { instruments: linkedPnidState?.instruments ?? [] })
                        : null;
                      const keepOpc =
                        Boolean(nextValue)
                        && effectiveContext?.context_type === 'node'
                        && effectiveContext.context_id === nextValue;
                      return {
                        ...prev,
                        target_node_id: nextValue || null,
                        target_opc_instrument_id: keepOpc ? prev.target_opc_instrument_id ?? null : null,
                        target_opc_tag: keepOpc ? prev.target_opc_tag ?? null : null,
                        target_line_no_snapshot: selectedNode?.line_no || null,
                      };
                    });
                  }}
                  options={linkedPnidNodeOptions}
                  placeholder={linkedPnidLoading ? 'Loading target lines...' : 'Select target line...'}
                  allowCustom={false}
                  disabled={readOnly || linkedPnidLoading || Boolean(linkedPnidError)}
                />
                {linkedPnidNodeStatusMessage && (
                  <div style={{ marginTop: 4, fontSize: 10, color: linkedPnidError ? '#b91c1c' : '#92400e' }}>
                    {linkedPnidNodeStatusMessage}
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                <label style={{ fontSize: 12 }}>
                  Link status
                  <select
                    value={pnidHyperlinkDraft.status ?? 'confirmed'}
                    onChange={(e) => {
                      setPnidHyperlinkDraft((prev) => prev ? {
                        ...prev,
                        status: e.target.value as 'confirmed' | 'needs_review',
                      } : prev);
                    }}
                    disabled={readOnly}
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: 8,
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 12,
                      boxSizing: 'border-box',
                  }}
                >
                    <option value="confirmed">Confirmed</option>
                    <option value="needs_review">Needs review</option>
                  </select>
                </label>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const getPfdDocumentLabel = (row: PnidLinkedPfdStreamRef): string => {
    const matchedDoc = (allDocuments ?? []).find((doc) => doc.id === row.pfd_file_id);
    return (
      matchedDoc?.pnid_no
      || row.pfd_no
      || matchedDoc?.original_filename
      || matchedDoc?.filename
      || row.pfd_file_id
      || '-'
    );
  };

  const renderLinkedPfdStreamSection = () => (
    <div
      style={{
        padding: 10,
        background: '#f9fafb',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Linked PFD Stream</div>
      </div>
      {!attachedStreamNode ? (
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          Attach this OPC to a stream to see linked PFD data.
        </div>
      ) : linkedPfdStreamRows.length === 0 ? (
        <div style={{ fontSize: 11, color: '#6b7280' }}>No linked PFD stream data on this stream.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {linkedPfdStreamRows.map((row, index) => {
            const targetState = linkedDocumentStates[row.pfd_file_id];
            const targetNode = targetState?.nodes.find((node) => node.id === row.node_id);
            const sourceOpcTag = row.source_opc_tag || getSourceOpcTag(instrument, pfdValues.instrument_text);
            const targetOpcTag = row.target_opc_tag || getNodeOpcTag(targetState?.instruments ?? [], row.node_id);
            const targetStreamLabel =
              targetNode?.line_no
              || row.target_line_no_snapshot
              || (targetNode ? `Stream ${targetNode.node_no}` : '-');
            return (
              <div
                key={`${row.pfd_file_id}:${row.node_id}:${index}`}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  fontSize: 11,
                  color: '#6b7280',
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 6,
                  overflowWrap: 'anywhere',
                }}
              >
                <div>
                  <strong>Source OPC:</strong> {formatStoredValue(sourceOpcTag)}
                </div>
                <div>
                  <strong>Target PFD:</strong> {formatStoredValue(getPfdDocumentLabel(row))}
                </div>
                <div>
                  <strong>Target OPC:</strong> {formatStoredValue(targetOpcTag)}
                </div>
                <div>
                  <strong>Target stream:</strong> {formatStoredValue(targetStreamLabel)}
                </div>
                <div>
                  <strong>Status:</strong> {formatLinkStatus(row.status)}
                </div>
                {row.pfd_file_id && row.node_id && onOpenLinkedDocument && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => onOpenLinkedDocument({ docId: row.pfd_file_id, targetNodeId: row.node_id, newTab: true })}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: '1px solid #bfdbfe',
                        background: '#fff',
                        color: '#1d4ed8',
                        cursor: 'pointer',
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Open in new tab
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Keep a stable reference to the latest renderLinkedPnidSection so the (memoized,
  // stable-identity) pfd_hyperlink custom renderer never remounts its inputs.
  const renderLinkedPnidSectionRef = useRef<() => React.ReactNode>(renderLinkedPnidSection);
  renderLinkedPnidSectionRef.current = renderLinkedPnidSection;

  // Same reason: the tag renderer below reads the document through a ref so the
  // memoized map keeps its empty dep list.
  const currentDocIdRef = useRef<string | null>(currentDocId ?? null);
  currentDocIdRef.current = currentDocId ?? null;

  // Custom field renderers for the PFD instrument form. Stable identity (deps: [])
  // so typing in an input does not remount the field. All dynamic data is read from
  // props (value/onChange/values/readOnly) or the ref above.
  const pfdCustomRenderers = useMemo<CustomRendererMap>(() => ({
    instrument_text: ({ value, onChange, values, readOnly }) => {
      const autoTag = PFD_AUTO_TAG_TYPES.has(
        typeof values.instrument_type === 'string' ? values.instrument_type : '',
      );
      if (autoTag) {
        return (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Tag</div>
            <input
              value={typeof values.generated_id === 'string' ? values.generated_id : ''}
              readOnly
              placeholder="Generated on save"
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 12,
                boxSizing: 'border-box',
                background: '#f9fafb',
                color: '#374151',
                cursor: 'default',
              }}
            />
            <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
              System-generated tag. Updates after save when type or attachment changes.
            </div>
          </div>
        );
      }
      return (
        <label style={{ display: 'block', fontSize: 12 }}>
          <span style={{ fontWeight: 500 }}>Tag</span>
          <TagAutocompleteInput
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
            docId={currentDocIdRef.current}
            kind="instrument"
            placeholder="e.g., FV-101"
            disabled={readOnly}
            style={{ marginTop: 4 }}
          />
        </label>
      );
    },
    subtype: ({ value, onChange, values, readOnly }) => {
      const type = typeof values.instrument_type === 'string' ? values.instrument_type : '';
      const options = pfdSubtypeOptions(type);
      if (options.length === 0) return null;
      return (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{type === 'OPC' ? 'Direction' : 'Subtype'}</div>
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
            style={{
              width: '100%',
              padding: 8,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              fontSize: 12,
              boxSizing: 'border-box',
            }}
          >
            <option value="">-- None --</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    },
    pnid_hyperlink: () => renderLinkedPnidSectionRef.current(),
  }), []);

  const renderTagStatusControl = () => {
    const options: Array<{ value: InstrumentTagStatus; label: string }> = [
      { value: 'tagged', label: 'Tagged' },
      { value: 'untagged', label: 'Untagged/generated' },
    ];
    return (
      <div
        style={{
          padding: 10,
          borderRadius: 6,
          border: '1px solid #e5e7eb',
          background: '#fff',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
          Tag Source
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {options.map((option) => {
            const selected = tagStatus === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setTagStatus(option.value)}
                disabled={readOnly}
                style={{
                  padding: '7px 8px',
                  borderRadius: 6,
                  border: `1px solid ${selected ? '#2563eb' : '#d1d5db'}`,
                  background: selected ? '#dbeafe' : '#fff',
                  color: selected ? '#1d4ed8' : '#374151',
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  whiteSpace: 'normal',
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const headerColor = '#eff6ff';
  const instrumentText = typeof core.instrument_text === 'string' ? core.instrument_text : '';
  const instrumentType = typeof core.instrument_type === 'string' ? core.instrument_type : '';
  const effectiveInstrumentType = instrumentType || instrument.instrument_type || '';
  const shouldShowPnidHyperlinkSection = isOpcInstrumentType(effectiveInstrumentType) || Boolean(pnidHyperlinkDraft);
  const pfdInstrumentType = typeof pfdValues.instrument_type === 'string' && pfdValues.instrument_type
    ? pfdValues.instrument_type
    : instrument.instrument_type || '';
  const shouldShowPfdStreamLinksSection = isPfdMode && isOpcInstrumentType(pfdInstrumentType);
  const generatedId = instrument.generated_id || '';
  const draftIdentity = {
    instrument_text: instrumentText,
    generated_id: generatedId,
    instrument_type: instrumentType || instrument.instrument_type,
  };
  const title = getInstrumentPrimaryLabel(draftIdentity);
  const subtitle = instrumentType ? getInstrumentDisplayName(instrumentType) : undefined;
  const branchNotes = typeof core.notes === 'string' ? core.notes : '';
  const showGeneratedId = usesGeneratedIdAsPrimaryLabel(draftIdentity) && Boolean(generatedId);

  return (
    <DraggablePanel
      position={position}
      width={INSTRUMENT_EDITOR_WIDTH}
      contentMaxHeight="min(70vh, 720px)"
      title={title}
      subtitle={subtitle}
      headerColor={headerColor}
      onClose={onClose}
      headerActions={onEditRegions && (
        <button
          type="button"
          data-no-drag="true"
          onClick={() => onEditRegions(instrument.id)}
          disabled={readOnly || latestInstrumentLocked}
          title="Redraw this marking's regions on the sheet"
          style={{
            padding: '4px 10px',
            borderRadius: 6,
            border: '1px solid #2563eb',
            background: '#eff6ff',
            color: '#1d4ed8',
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: readOnly || latestInstrumentLocked ? 'not-allowed' : 'pointer',
          }}
        >
          Edit regions
        </button>
      )}
      footer={
        <>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'nowrap',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {onMarkReviewed && (
              <button
                type="button"
                onClick={() => void handleMarkReviewedClick()}
                disabled={isReviewed || saving || reviewSaving || latestInstrumentLocked}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #86efac',
                  borderRadius: 6,
                  background: isReviewed ? '#dcfce7' : '#f0fdf4',
                  color: '#166534',
                  cursor: isReviewed || saving || reviewSaving || latestInstrumentLocked ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: latestInstrumentLocked ? 0.6 : 1,
                }}
              >
                {reviewSaving ? 'Saving...' : isReviewed ? 'Reviewed' : 'Mark as Reviewed'}
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={saving || readOnly}
              style={{
                padding: '6px 12px',
                border: '1px solid #fca5a5',
                borderRadius: 6,
                background: deleteConfirm ? '#ef4444' : '#fff',
                color: deleteConfirm ? '#fff' : '#ef4444',
                cursor: readOnly ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 500,
                opacity: readOnly ? 0.5 : 1,
              }}
              onBlur={() => setDeleteConfirm(false)}
            >
              {deleteConfirm ? 'Confirm Delete' : 'Delete'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '6px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || readOnly}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: 6,
                background: '#2563eb',
                color: '#fff',
                cursor: readOnly ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 500,
                opacity: saving || readOnly ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </>
      }
    >
      {(latestInstrumentLoading || latestInstrumentError) && (
        <div style={{
          padding: '8px 10px',
          borderRadius: 6,
          border: `1px solid ${latestInstrumentError ? '#fecaca' : '#bfdbfe'}`,
          background: latestInstrumentError ? '#fef2f2' : '#eff6ff',
          color: latestInstrumentError ? '#991b1b' : '#1e40af',
          fontSize: 12,
          fontWeight: 500,
        }}>
          {latestInstrumentError ?? 'Loading latest instrument data. Editing is disabled until the backend copy is ready.'}
        </div>
      )}

      {saveError && (
        <div style={{
          padding: '8px 10px',
          borderRadius: 6,
          border: '1px solid #fecaca',
          background: '#fef2f2',
          color: '#991b1b',
          fontSize: 12,
        }}>
          {saveError}
        </div>
      )}

      {showGeneratedId && (
        <div style={{
          padding: '8px 10px',
          borderRadius: 6,
          border: '1px solid #e5e7eb',
          background: '#f8fafc',
          fontSize: 12,
          color: '#374151',
        }}>
          <div style={{ fontWeight: 600, color: '#111827' }}>Generated ID</div>
          <div style={{ marginTop: 2, color: '#6b7280' }}>{generatedId}</div>
        </div>
      )}

      {effectiveEditorMode === 'instrument' && renderTagStatusControl()}

      {instrument.provenance && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          background: '#f9fafb',
          overflow: 'hidden',
        }}>
          <div
            onClick={() => setProvenanceExpanded(!provenanceExpanded)}
            style={{
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 11,
              fontWeight: 500,
              color: '#6b7280',
            }}
          >
            <span>
              Source: {PROVENANCE_LABELS[instrument.provenance.source] || instrument.provenance.source}
            </span>
            <span style={{ fontSize: 9 }}>{provenanceExpanded ? '▲' : '▼'}</span>
          </div>
          {provenanceExpanded && (
            <div style={{ padding: '6px 10px', borderTop: '1px solid #e5e7eb', fontSize: 11 }}>
              {Object.entries(instrument.provenance.metadata).map(([key, val]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <span style={{ color: '#6b7280' }}>{key.replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 500, color: '#1f2937' }}>
                    {typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(3)) : String(val)}
                  </span>
                </div>
              ))}
              {instrument.created_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <span style={{ color: '#6b7280' }}>created</span>
                  <span style={{ fontWeight: 500, color: '#1f2937' }}>
                    {new Date(instrument.created_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isPfdMode && (
        <div style={{
          display: 'flex',
          gap: 8,
          padding: 8,
          background: '#f8fafc',
          borderRadius: 6,
          border: '1px solid #e2e8f0',
        }}>
          <label style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 6,
            cursor: readOnly ? 'not-allowed' : 'pointer',
            background: shared.mode === 'instrument' ? '#dbeafe' : 'white',
            border: `1px solid ${shared.mode === 'instrument' ? '#3b82f6' : '#e5e7eb'}`,
            fontSize: 12,
          }}>
            <input
              type="radio"
              name="editor_mode"
              checked={shared.mode === 'instrument'}
              onChange={handleSelectInstrumentMode}
              disabled={readOnly}
            />
            <div>
              <div style={{ fontWeight: 500 }}>Instrument</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Valve, transmitter, etc.</div>
            </div>
          </label>
          <label style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 6,
            cursor: readOnly ? 'not-allowed' : 'pointer',
            background: shared.mode === 'branch_join' ? '#fef3c7' : 'white',
            border: `1px solid ${shared.mode === 'branch_join' ? '#f59e0b' : '#e5e7eb'}`,
            fontSize: 12,
          }}>
            <input
              type="radio"
              name="editor_mode"
              checked={shared.mode === 'branch_join'}
              onChange={handleSelectBranchMode}
              disabled={readOnly}
            />
            <div>
              <div style={{ fontWeight: 500 }}>Branch/Join</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Line splitting or joining</div>
            </div>
          </label>
        </div>
      )}

      {/* A branch or join owns no asset, so it has no plant to be owned by. */}
      {!isPfdMode && shared.mode === 'instrument' && (
        <div>
          {/* The label points at the select rather than wrapping it, so the
              picker's own status line stays out of the field's name. */}
          <label
            htmlFor={OWNER_DOMAIN_SELECT_ID}
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--raven-text-primary)',
            }}
          >
            Domain
          </label>
          <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 2 }}>
            The plant that owns this instrument.
          </div>
          <DomainSelect
            id={OWNER_DOMAIN_SELECT_ID}
            storedValue={shared.owner_domain_id}
            onChange={(next) => setSharedField('owner_domain_id', next)}
            drawingDomainId={drawingDomainId}
            drawingPlantName={currentPlant ?? null}
            disabled={readOnly}
            style={{
              width: '100%',
              marginTop: 4,
              padding: 8,
              border: '1px solid var(--raven-divider)',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 400,
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {effectiveEditorMode === 'instrument' && (
        <>
          <div style={{
            padding: 10,
            background: '#f9fafb',
            borderRadius: 6,
            border: '1px solid #e5e7eb',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Attach To</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {(['node', 'equipment', 'instrument'] as const).map((contextType) => (
                <button
                  key={contextType}
                  onClick={() => {
                    setShared((prev) => ({
                      ...prev,
                      context_type: prev.context_type === contextType ? null : contextType,
                      context_id: prev.context_type === contextType ? null : null,
                    }));
                  }}
                  disabled={readOnly}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: readOnly ? 'not-allowed' : 'pointer',
                    background: shared.context_type === contextType ? '#dbeafe' : 'white',
                    border: `1px solid ${shared.context_type === contextType ? '#3b82f6' : '#d1d5db'}`,
                    color: shared.context_type === contextType ? '#1d4ed8' : '#374151',
                  }}
                >
                  {contextType === 'node' ? 'Node' : contextType === 'equipment' ? 'Equipment' : 'Instrument'}
                </button>
              ))}
            </div>
            {shared.context_type === 'node' && (
              <>
                <AutocompleteDropdown
                  value={shared.context_id ?? ''}
                  onChange={(value) => setSharedField('context_id', value || null)}
                  options={nodeOptions}
                  placeholder="Select line/node..."
                  allowCustom={false}
                  onHoverOption={onHighlightEntity}
                  disabled={readOnly}
                />
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 8,
                  fontSize: 11,
                  color: '#374151',
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={shared.present_on_node}
                    onChange={(e) => setSharedField('present_on_node', e.target.checked)}
                    disabled={readOnly}
                    style={{ accentColor: '#3b82f6' }}
                  />
                  Present on node
                </label>
              </>
            )}
            {shared.context_type === 'equipment' && (
              <AutocompleteDropdown
                value={shared.context_id ?? ''}
                onChange={(value) => setSharedField('context_id', value || null)}
                options={equipmentOptions}
                placeholder="Select equipment..."
                allowCustom={false}
                onHoverOption={onHighlightEntity}
                disabled={readOnly}
              />
            )}
            {shared.context_type === 'instrument' && (
              <AutocompleteDropdown
                value={shared.context_id ?? ''}
                onChange={(value) => setSharedField('context_id', value || null)}
                options={instrumentContextOptions}
                placeholder="Select parent instrument..."
                allowCustom={false}
                onHoverOption={onHighlightEntity}
                disabled={readOnly}
              />
            )}
            {!shared.context_type && (
              <div style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>
                Unattached — select a context above
              </div>
            )}
          </div>

          {isPfdMode ? (
            pfdInstrumentSchema ? (
              <>
                <SchemaFieldRenderer
                  sections={pfdInstrumentSchema.sections}
                  values={pfdValues}
                  setField={setPfdField}
                  customRenderers={pfdCustomRenderers}
                  readOnly={readOnly}
                />
                {shouldShowPfdStreamLinksSection && renderLinkedPfdStreamSection()}
              </>
            ) : (
              <div style={{ padding: 10, color: '#6b7280', fontSize: 12 }}>Loading PFD form...</div>
            )
          ) : (
            visibleCoreFields.map((field) => (
              <React.Fragment key={field.id}>
                {renderCoreField(field)}
                {field.storage_key === 'instrument_text' && renderDescriptionField()}
                {field.storage_key === 'instrument_text' && renderLinkedInstrumentSection()}
                {field.storage_key === 'instrument_text' && shouldShowPnidHyperlinkSection && renderLinkedPnidSection()}
              </React.Fragment>
            ))
          )}
        </>
      )}

      {/* Outside the schema-driven form: the sets have their own store and their own save. */}
      {effectiveEditorMode === 'instrument' && !isPfdMode && currentDocId && currentDomainId && (
        <RedundancySetSection
          docId={currentDocId}
          domainId={currentDomainId}
          savedTag={instrument.tag_status === 'tagged' ? instrument.instrument_text ?? '' : ''}
          kind="instrument"
          readOnly={readOnly}
        />
      )}

      {effectiveEditorMode === 'branch_join' && (
        <>
          <div style={{
            padding: 12,
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 6,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 10 }}>
              Branch/Join Point
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Node Number (required)</div>
              <AutocompleteDropdown
                value={shared.branch_ref}
                onChange={(value) => setSharedField('branch_ref', value)}
                placeholder="Select existing node..."
                allowCustom={false}
                options={branchNodeOptions}
                disabled={readOnly}
              />
              {branchNodeOptions.length === 0 && (
                <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 4, padding: '4px 8px', background: '#fffbeb', borderRadius: 4 }}>
                  Create the target node first before setting branch point
                </div>
              )}
            </div>

            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Direction (required)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '10px 12px',
                borderRadius: 6,
                cursor: readOnly ? 'not-allowed' : 'pointer',
                background: shared.branch_direction === 'IN' ? '#dcfce7' : 'white',
                border: `2px solid ${shared.branch_direction === 'IN' ? '#22c55e' : '#e5e7eb'}`,
                fontWeight: shared.branch_direction === 'IN' ? 600 : 400,
              }}
              onClick={(e) => { if (readOnly) e.preventDefault(); }}
              >
                <input
                  type="radio"
                  name="branch_direction"
                  value="IN"
                  checked={shared.branch_direction === 'IN'}
                  onChange={() => setSharedField('branch_direction', 'IN')}
                  disabled={readOnly}
                  style={{ display: 'none' }}
                />
                <span>IN</span>
                <span style={{ fontSize: 10, color: '#6b7280' }}>(joining)</span>
              </label>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '10px 12px',
                borderRadius: 6,
                cursor: readOnly ? 'not-allowed' : 'pointer',
                background: shared.branch_direction === 'OUT' ? '#fef3c7' : 'white',
                border: `2px solid ${shared.branch_direction === 'OUT' ? '#f59e0b' : '#e5e7eb'}`,
                fontWeight: shared.branch_direction === 'OUT' ? 600 : 400,
              }}
              onClick={(e) => { if (readOnly) e.preventDefault(); }}
              >
                <input
                  type="radio"
                  name="branch_direction"
                  value="OUT"
                  checked={shared.branch_direction === 'OUT'}
                  onChange={() => setSharedField('branch_direction', 'OUT')}
                  disabled={readOnly}
                  style={{ display: 'none' }}
                />
                <span>OUT</span>
                <span style={{ fontSize: 10, color: '#6b7280' }}>(splitting)</span>
              </label>
            </div>
          </div>

          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Notes</div>
            <textarea
              value={branchNotes}
              onChange={(e) => setCoreField('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
              readOnly={readOnly}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                fontSize: 12,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
              Attached to {title || 'this instrument'}
            </div>
          </div>
        </>
      )}
    </DraggablePanel>
  );
};

export default InstrumentEditor;
